export const designThinkingModule = {
  id: "ips-design-thinking",
  title: "Design Thinking Methodology",
  description: "Apply human-centered design principles to solve complex problems and drive innovation across any industry.",
  content: `
## What You'll Learn
By the end of this module, you will be able to:
- Apply the five-stage design thinking process to tackle complex challenges
- Use empathy mapping and user research techniques to understand stakeholder needs deeply
- Generate innovative solutions through structured ideation and rapid prototyping
- Test and iterate solutions based on user feedback and real-world constraints

## Prerequisites
- Experience with problem-solving in professional contexts
- Willingness to engage in creative and collaborative thinking exercises

## Overview
Design thinking is a proven methodology for innovation used by leading companies like Apple, Google, and IDEO. Organizations that apply design thinking principles show 56% higher revenue growth and 75% better customer satisfaction. This approach transforms how you understand problems and develop solutions.

---

## Key Concepts

### The Five-Stage Design Thinking Process
Design thinking follows a structured yet flexible process: Empathize, Define, Ideate, Prototype, and Test. This human-centered approach ensures solutions address real needs rather than assumed problems.

#### Why It Matters
Teams using design thinking methodology solve problems 60% faster and generate solutions with 40% higher user adoption rates. The structured approach prevents common pitfalls like solving the wrong problem or developing solutions nobody wants.

#### Best Practices
- Start with deep empathy research before jumping to solutions
- Define problems clearly using "How Might We" statements
- Generate many ideas before evaluating any single option
- Build quick, testable prototypes rather than perfect solutions
- Iterate based on user feedback rather than internal assumptions

### Human-Centered Problem Framing
Effective problem-solving begins with understanding the human experience and unmet needs. This perspective often reveals opportunities that technical or business-focused approaches miss.

#### Industry Usage
Companies like Airbnb and Netflix use human-centered problem framing to identify market opportunities and improve user experiences. This approach led to breakthrough innovations that disrupted entire industries.

---

## Practical Examples

### Example 1: Empathy Map Creation
Understand stakeholder perspectives through systematic empathy research.

**Empathy Map Framework:**
- **Says**: Direct quotes and defining words from user interviews
- **Thinks**: Thoughts, beliefs, and preoccupations revealed through observation
- **Does**: Actions and behaviors in relevant contexts
- **Feels**: Emotions and feelings expressed or observed

**Application**: Interview 3-5 users, observe their actual behavior, then synthesize insights into clear empathy maps before defining the problem.

### Example 2: "How Might We" Problem Statements
Transform challenges into opportunity-focused questions that inspire solutions.

**Poor Problem Statement**: "Our customer service is too slow"

**Better HMW Statement**: "How might we help customers resolve their issues quickly while feeling heard and valued?"

**Framework**: "How might we [action] [stakeholder] [context/constraint] so that [desired outcome]?"

💡 **Pro Tip**: Use the "5 Whys" technique before creating HMW statements to ensure you're solving root causes rather than symptoms.

---

## Industry Adoption

### Why Companies Choose This Approach
Organizations adopt design thinking because it reduces product failure rates by 65% and increases innovation success by 85%. The methodology works across industries - from healthcare to finance to manufacturing.

### Popular Tools and Libraries
- **Miro/Mural**: Digital whiteboarding for collaborative design thinking sessions
- **Figma**: Rapid prototyping and design iteration tools
- **Typeform**: User research and feedback collection platforms

### Comparison with Alternatives
| Approach | Advantages | Use Cases |
|----------|------------|-----------|
| Design Thinking | User-centered, creative, systematic | Innovation projects, user experience improvements, complex problems |
| Lean Startup | Fast, data-driven, resource-efficient | Product development, market validation, startup environments |

---

## Key Takeaways

✅ **Essential Points**:
- Five-stage process provides structure while maintaining creative flexibility
- Empathy research prevents solving wrong problems or creating unwanted solutions
- "How Might We" statements reframe challenges as opportunities for innovation
- Rapid prototyping and testing validate ideas before major resource investment

⚠️ **Common Pitfalls**:
- Skipping empathy research and assuming you understand user needs
- Falling in love with your first idea instead of exploring multiple solutions

## Next Steps
Next, you'll learn creative problem-solving techniques that complement design thinking with additional frameworks for generating breakthrough solutions.

---
`,
  duration: "55 minutes",
  difficulty: "intermediate" as const,
  objectives: [
    "Master five-stage design thinking process",
    "Apply empathy mapping techniques",
    "Create opportunity-focused problem statements",
    "Implement rapid prototyping methods"
  ],
  keyTopics: [
    "Design Thinking Process",
    "Empathy Research",
    "Problem Framing",
    "Ideation Techniques",
    "Rapid Prototyping"
  ]
};